import { FC } from "react";
import Button from "../ui/Button";
import { Row } from "../ui/table";
import { IEntityChangeGroup } from "../../interfaces/endpoints/IEntityChangeLog.types";
import ChangeLine from "./ChangeLine";
import EntityCell from "./EntityCell";
import {
  describeWho,
  groupActionLabel,
  groupActionStyle,
  summarizeGroup,
  summaryLines,
  undoingChangeId,
  undoneChangeId,
  wasUndone
} from "./changeHistoryGroups";
import { describeRevertedBy, formatUtcDate, formatUtcTime } from "./changeHistoryLabels";

interface IActionRow {
  group: IEntityChangeGroup;
  currentEntityName: string;
  currentEntityId: string;
  onPickEntity: (entityName: string, entityId: number) => void;
  // One record in full; for an action that wrote several, the list of them.
  onOpenRow: (logId: number) => void;
  onOpenAction: (group: IEntityChangeGroup) => void;
}

// One action of the admin as a table row: when, what it was about, what it did, and who did it.
const ActionRow: FC<IActionRow> = ({
  group,
  currentEntityName,
  currentEntityId,
  onPickEntity,
  onOpenRow,
  onOpenAction
}) => {
  const single = group.rowCount === 1;
  const lines = summaryLines(group);
  const undone = wasUndone(group);
  // Both sides of an undo point at each other, so one click moves between them.
  const otherSide = undoneChangeId(group) ?? undoingChangeId(group);
  const undoneNote = undone ? describeRevertedBy(group.rows[0]) : null;

  return (
    <Row
      className={undone ? "opacity-60" : ""}
      when={
        <span className="block leading-tight">
          {formatUtcDate(group.changedAtUtc)}
          <span className="block text-xs text-gray-500">{formatUtcTime(group.changedAtUtc)}</span>
        </span>
      }
      entity={
        <EntityCell
          row={single ? group.rows[0] : group}
          currentEntityName={currentEntityName}
          currentEntityId={currentEntityId}
          onPickEntity={onPickEntity}
        />
      }
      action={
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${groupActionStyle(group)}`}>
          {groupActionLabel(group)}
        </span>
      }
      who={describeWho(group.actorName, group.actorType, group.actorId)}
      changes={
        <span className="block max-w-sm" title={summarizeGroup(group)}>
          {lines.length === 0
            ? "—"
            : lines.map((line) => <ChangeLine key={line} line={line} compact />)}
          {undoneNote && otherSide !== null && (
            <Button
              btnType="custom"
              padding="px-0 py-0"
              fontWeight="font-normal"
              className="text-xs text-primary hover:underline truncate block text-left"
              onClick={() => onOpenRow(otherSide)}
            >
              {undoneNote}
            </Button>
          )}
        </span>
      }
      details={
        group.rowCount === 0 ? null : (
          <Button
            btnType="custom"
            padding="px-3 py-1"
            className="text-primary"
            onClick={() => (single ? onOpenRow(group.rows[0].id) : onOpenAction(group))}
          >
            Detajet
          </Button>
        )
      }
    />
  );
};

export default ActionRow;
